// ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING - TABLEAU DE BORD CULTURES ANNUEL
// ═══════════════════════════════════════════════════════════════════════════
// 73+ Papy - Janvier 2026
// Vue timeline/Gantt de toutes les cultures sur l'année
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from 'react';
import {
    Calendar, Leaf, RefreshCw, ChevronLeft, ChevronRight,
    AlertTriangle, CheckCircle, Clock, Sprout, Sun, CloudRain,
    Filter, Download, Plus
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Noms des mois
const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const MOIS_COMPLETS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

// Couleurs des stades
const COULEURS_STADES = {
    'INIT': { bg: 'bg-blue-200', text: 'text-blue-800', label: 'Germination' },
    'DEV': { bg: 'bg-green-300', text: 'text-green-800', label: 'Développement' },
    'MID': { bg: 'bg-green-500', text: 'text-white', label: 'Mi-saison' },
    'FIN': { bg: 'bg-orange-400', text: 'text-orange-900', label: 'Maturation' },
    'RECOLTE': { bg: 'bg-amber-500', text: 'text-amber-900', label: 'Récolte' }
};

const TableauCulturesAnnuel = () => {
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [cultures, setCultures] = useState([]);
    const [parcelles, setParcelles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtreZone, setFiltreZone] = useState('toutes');
    const [filtreStatut, setFiltreStatut] = useState('tous');

    // ─── Chargement des données ───────────────────────────────────────────
    useEffect(() => {
        loadData();
    }, [annee]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Charger les cultures
            const culturesRes = await fetch(`${API_BASE}/cultures?annee=${annee}`);
            const culturesData = await culturesRes.json();
            
            // Charger les parcelles avec détails
            const parcellesRes = await fetch(`${API_BASE}/parcelles-gestion/liste-complete`);
            const parcellesData = await parcellesRes.json();

            if (culturesData.success) {
                setCultures(culturesData.data || []);
            }
            if (parcellesData.success) {
                setParcelles(parcellesData.data || []);
            }
        } catch (err) {
            console.error('Erreur chargement:', err);
        } finally {
            setLoading(false);
        }
    };

    // ─── Calcul des données pour le Gantt ─────────────────────────────────
    const culturesAvecTimeline = useMemo(() => {
        return cultures.map(culture => {
            // Trouver la parcelle associée
            const parcelle = parcelles.find(p => p.id === culture.parcelle_id);
            
            // Date de début
            const dateDebut = new Date(culture.date_reelle_semis || culture.date_reelle_repiquage || culture.date_prevue_semis);
            const moisDebut = dateDebut.getMonth();
            const jourDebut = dateDebut.getDate();
            
            // Durée totale estimée (en jours)
            const dureeTotale = culture.duree_cycle_jours || 120;
            
            // Calculer le mois de fin
            const dateFin = new Date(dateDebut);
            dateFin.setDate(dateFin.getDate() + dureeTotale);
            const moisFin = dateFin.getMonth();
            
            // Position dans l'année (0-12)
            const debutPct = (moisDebut + jourDebut / 30) / 12 * 100;
            const finPct = Math.min(100, (moisFin + dateFin.getDate() / 30) / 12 * 100);
            const largeurPct = finPct - debutPct;

            // Stade actuel
            const joursEcoules = Math.floor((new Date() - dateDebut) / (1000 * 60 * 60 * 24));
            let stadeActuel = 'INIT';
            if (joursEcoules > dureeTotale * 0.8) stadeActuel = 'FIN';
            else if (joursEcoules > dureeTotale * 0.5) stadeActuel = 'MID';
            else if (joursEcoules > dureeTotale * 0.2) stadeActuel = 'DEV';

            return {
                ...culture,
                parcelle,
                dateDebut,
                dateFin,
                moisDebut,
                moisFin,
                debutPct,
                largeurPct,
                stadeActuel,
                joursEcoules,
                dureeTotale,
                progression: Math.min(100, Math.round((joursEcoules / dureeTotale) * 100))
            };
        });
    }, [cultures, parcelles]);

    // ─── Regrouper par parcelle ───────────────────────────────────────────
    const parcellesAvecCultures = useMemo(() => {
        const grouped = {};
        
        parcelles.forEach(parcelle => {
            const culturesParcelle = culturesAvecTimeline.filter(c => c.parcelle_id === parcelle.id);
            if (culturesParcelle.length > 0 || filtreStatut === 'tous') {
                const key = `${parcelle.tranche_nom}-${parcelle.id}`;
                grouped[key] = {
                    parcelle,
                    cultures: culturesParcelle
                };
            }
        });
        
        return Object.values(grouped).sort((a, b) => 
            a.parcelle.nom_complet?.localeCompare(b.parcelle.nom_complet || '')
        );
    }, [parcelles, culturesAvecTimeline, filtreStatut]);

    // ─── Zones uniques pour le filtre ─────────────────────────────────────
    const zones = useMemo(() => {
        const uniqueZones = new Set(parcelles.map(p => p.tranche_nom));
        return ['toutes', ...Array.from(uniqueZones)];
    }, [parcelles]);

    // ─── Stats ────────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const actives = cultures.filter(c => !['termine', 'echec', 'abandonne'].includes(c.statut));
        const enCours = cultures.filter(c => ['seme', 'repique', 'croissance'].includes(c.statut));
        const surfaceTotale = actives.reduce((sum, c) => sum + parseFloat(c.surface_m2 || 0), 0);
        const alertes = cultures.filter(c => c.flags_count > 0).length;
        
        return {
            total: cultures.length,
            actives: actives.length,
            enCours: enCours.length,
            surfaceTotale,
            alertes
        };
    }, [cultures]);

    // ─── Mois actuel ──────────────────────────────────────────────────────
    const moisActuel = new Date().getMonth();
    const jourActuel = new Date().getDate();
    const positionAujourdhui = (moisActuel + jourActuel / 30) / 12 * 100;

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU
    // ═══════════════════════════════════════════════════════════════════════
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
                <span className="ml-2 text-gray-600">Chargement du tableau...</span>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Calendar className="w-7 h-7 text-green-600" />
                    🌱 Tableau de Bord Cultures {annee}
                </h1>
                
                <div className="flex items-center gap-3">
                    {/* Navigation année */}
                    <div className="flex items-center bg-white rounded-lg shadow px-2">
                        <button
                            onClick={() => setAnnee(a => a - 1)}
                            className="p-2 hover:bg-gray-100 rounded"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="px-4 font-bold text-lg">{annee}</span>
                        <button
                            onClick={() => setAnnee(a => a + 1)}
                            className="p-2 hover:bg-gray-100 rounded"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                    
                    <button
                        onClick={loadData}
                        className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow hover:bg-gray-50"
                    >
                        <RefreshCw className="w-4 h-4" /> Actualiser
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-5 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow p-4 text-center">
                    <div className="text-3xl font-bold text-green-600">{stats.total}</div>
                    <div className="text-sm text-gray-500">Cultures {annee}</div>
                </div>
                <div className="bg-white rounded-xl shadow p-4 text-center">
                    <div className="text-3xl font-bold text-blue-600">{stats.enCours}</div>
                    <div className="text-sm text-gray-500">En cours</div>
                </div>
                <div className="bg-white rounded-xl shadow p-4 text-center">
                    <div className="text-3xl font-bold text-purple-600">{stats.surfaceTotale.toFixed(0)}</div>
                    <div className="text-sm text-gray-500">m² cultivés</div>
                </div>
                <div className="bg-white rounded-xl shadow p-4 text-center">
                    <div className="text-3xl font-bold text-orange-600">{stats.alertes}</div>
                    <div className="text-sm text-gray-500">Alertes</div>
                </div>
                <div className="bg-white rounded-xl shadow p-4 text-center">
                    <div className="text-3xl font-bold text-gray-600">{parcelles.length}</div>
                    <div className="text-sm text-gray-500">Parcelles</div>
                </div>
            </div>

            {/* Filtres */}
            <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <select
                        value={filtreZone}
                        onChange={(e) => setFiltreZone(e.target.value)}
                        className="border rounded-lg px-3 py-2 text-sm"
                    >
                        {zones.map(z => (
                            <option key={z} value={z}>{z === 'toutes' ? 'Toutes les zones' : z}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Timeline Gantt */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
                {/* Header mois */}
                <div className="flex border-b sticky top-0 bg-white z-10">
                    <div className="w-64 flex-shrink-0 p-3 bg-gray-50 font-semibold text-gray-700 border-r">
                        Parcelle / Culture
                    </div>
                    <div className="flex-1 flex">
                        {MOIS.map((mois, idx) => (
                            <div 
                                key={idx} 
                                className={`flex-1 text-center py-3 text-sm font-medium border-r ${
                                    idx === moisActuel ? 'bg-green-100 text-green-800' : 'text-gray-600'
                                }`}
                            >
                                {mois}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Lignes par parcelle */}
                {parcellesAvecCultures
                    .filter(item => filtreZone === 'toutes' || item.parcelle.tranche_nom === filtreZone)
                    .map((item, idx) => (
                    <div key={idx} className="border-b last:border-b-0">
                        {/* Ligne parcelle */}
                        <div className="flex bg-gray-50">
                            <div className="w-64 flex-shrink-0 p-3 border-r">
                                <div className="font-medium text-gray-800 flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${
                                        item.parcelle.tranche_nom?.includes('Serre') ? 'bg-blue-500' : 'bg-green-500'
                                    }`} />
                                    {item.parcelle.nom}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {item.parcelle.surface_m2}m² • {item.parcelle.tranche_nom}
                                </div>
                            </div>
                            <div className="flex-1 relative">
                                {/* Grille des mois */}
                                <div className="absolute inset-0 flex">
                                    {MOIS.map((_, idx) => (
                                        <div key={idx} className="flex-1 border-r border-gray-100" />
                                    ))}
                                </div>
                                {/* Ligne du jour actuel */}
                                {annee === new Date().getFullYear() && (
                                    <div 
                                        className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                                        style={{ left: `${positionAujourdhui}%` }}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Lignes cultures */}
                        {item.cultures.map((culture, cidx) => (
                            <div key={cidx} className="flex hover:bg-gray-50">
                                <div className="w-64 flex-shrink-0 p-2 pl-8 border-r">
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">{culture.variete_icone || '🌱'}</span>
                                        <div>
                                            <div className="text-sm font-medium text-gray-700 truncate">
                                                {culture.variete_nom || culture.code_culture}
                                            </div>
                                            <div className="text-xs text-gray-400">
                                                {culture.surface_m2}m² • J+{culture.joursEcoules}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-1 relative py-2 px-1">
                                    {/* Grille */}
                                    <div className="absolute inset-0 flex">
                                        {MOIS.map((_, idx) => (
                                            <div key={idx} className="flex-1 border-r border-gray-100" />
                                        ))}
                                    </div>
                                    
                                    {/* Barre de culture */}
                                    <div
                                        className={`absolute h-6 rounded-full flex items-center px-2 text-xs font-medium shadow-sm ${
                                            COULEURS_STADES[culture.stadeActuel]?.bg || 'bg-gray-300'
                                        } ${COULEURS_STADES[culture.stadeActuel]?.text || 'text-gray-700'}`}
                                        style={{
                                            left: `${culture.debutPct}%`,
                                            width: `${Math.max(5, culture.largeurPct)}%`,
                                            top: '50%',
                                            transform: 'translateY(-50%)'
                                        }}
                                        title={`${culture.variete_nom}: ${culture.dateDebut.toLocaleDateString('fr-FR')} → ${culture.dateFin.toLocaleDateString('fr-FR')}`}
                                    >
                                        <span className="truncate">
                                            {COULEURS_STADES[culture.stadeActuel]?.label}
                                        </span>
                                    </div>
                                    
                                    {/* Ligne du jour actuel */}
                                    {annee === new Date().getFullYear() && (
                                        <div 
                                            className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10"
                                            style={{ left: `${positionAujourdhui}%` }}
                                        />
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Message si pas de culture */}
                        {item.cultures.length === 0 && (
                            <div className="flex hover:bg-gray-50">
                                <div className="w-64 flex-shrink-0 p-2 pl-8 border-r text-gray-400 text-sm italic">
                                    Aucune culture
                                </div>
                                <div className="flex-1 relative">
                                    <div className="absolute inset-0 flex">
                                        {MOIS.map((_, idx) => (
                                            <div key={idx} className="flex-1 border-r border-gray-100" />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {/* Si aucune donnée */}
                {parcellesAvecCultures.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <Sprout className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Aucune culture pour {annee}</p>
                    </div>
                )}
            </div>

            {/* Légende */}
            <div className="mt-4 bg-white rounded-xl shadow p-4">
                <div className="flex items-center gap-6 flex-wrap">
                    <span className="text-sm font-medium text-gray-700">Légende :</span>
                    {Object.entries(COULEURS_STADES).map(([code, style]) => (
                        <div key={code} className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded ${style.bg}`} />
                            <span className="text-sm text-gray-600">{style.label}</span>
                        </div>
                    ))}
                    <div className="flex items-center gap-2 ml-4">
                        <div className="w-0.5 h-4 bg-red-500" />
                        <span className="text-sm text-gray-600">Aujourd'hui</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TableauCulturesAnnuel;